import { useMemo, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import TaskDrawer from "../../components/TaskDrawer";
import CalendarLegend from "../../components/CalendarLegend";
import DayTasksDrawer from "../../components/DayTasksDrawer";
import DayNotesDrawer from "../../components/DayNotesDrawer";
import CalendarNoteEditorDrawer from "../../components/CalendarNoteEditorDrawer";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import { listEmployeeTasks } from "../../api/employeeTaskService";
import { adminCalendarNotes, adminDeleteCalendarNote } from "../../api/calendarNoteService";
import { showApiError, showToast } from "../../utils/toast";
import { listEmployees } from "../../api/employeeService";
import { listAllBusinesses } from "../../api/businessService";
import { paginatedRows } from "../../utils/pagination";
import { taskState, STATUS_LABELS, countByState, legendStates, isOverdue } from "../../utils/taskCalendar";
import { nowIst, todayKeyIst } from "../../utils/formatters";

const MAX_VISIBLE = 4;

export default function CalendarPage() {
  const [cursor, setCursor] = useState(nowIst().startOf("month"));
  const [openTask, setOpenTask] = useState(null);
  const [openDay, setOpenDay] = useState(null); // { date, tasks } | null
  const [openNotes, setOpenNotes] = useState(null); // { date, notes } | null
  const [noteEditor, setNoteEditor] = useState(null); // null | { note?, start?, end? }
  const [employeeId, setEmployeeId] = useState("");
  // Business *is* the client now — one picker, not two.
  const [businessId, setBusinessId] = useState("");
  // Legend status filter — all checked by default (everything visible). The
  // checkbox states are the legend entries; cancelled has none so it's never hidden.
  const [selected, setSelected] = useState(() => new Set(legendStates()));
  const toggleState = (s) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });

  const from = cursor.startOf("month").format("YYYY-MM-DD");
  const to = cursor.endOf("month").format("YYYY-MM-DD");

  const tasks = useApi(
    () => {
      const params = { due_from: from, due_to: to, per_page: 500 };
      if (employeeId === "__unassigned__") params.unassigned = true;
      else if (employeeId) params.employee_id = employeeId;
      if (businessId) params.business_id = businessId;
      return listEmployeeTasks(params);
    },
    [from, to, employeeId, businessId],
  );
  const notesApi = useApi(
    () => adminCalendarNotes({
      from,
      to,
      ...(employeeId && employeeId !== "__unassigned__" ? { employee_id: employeeId } : {}),
    }),
    [from, to, employeeId],
  );
  const notes = Array.isArray(notesApi.data?.data) ? notesApi.data.data : [];

  const employees = useApi(() => listEmployees({ per_page: 200 }));
  const businesses = useApi(() => listAllBusinesses({ per_page: 500 }));
  const rows = paginatedRows(tasks.data);
  const employeeRows = paginatedRows(employees.data);
  const businessRows = paginatedRows(businesses.data);

  const businessOptions = useMemo(
    () => businessRows.map((b) => ({
      id: b.id,
      label: b.business_name,
      hint: b.client_id,
    })),
    [businessRows],
  );

  const employeeOptions = useMemo(() => [
    { id: "__unassigned__", label: "— Unassigned —" },
    ...employeeRows.map((e) => ({
      id: e.id,
      label: e.name,
      hint: e.department?.name,
    })),
  ], [employeeRows]);

  // States that have a checkbox; tasks whose base state has one are hidden when
  // it's unchecked, and overdue tasks are hidden when "overdue" is unchecked.
  const checkboxStates = useMemo(() => new Set(legendStates()), []);
  const todayKey = todayKeyIst();
  const visibleRows = useMemo(() => rows.filter((t) => {
    // Overdue tasks render with the overdue (red) style on top of their base
    // colour, so visually they're "overdue" — the Overdue checkbox alone
    // governs them, independent of their base-status checkbox.
    if (checkboxStates.has("overdue") && isOverdue(t, todayKey)) return selected.has("overdue");
    const base = taskState(t);
    if (checkboxStates.has(base)) return selected.has(base);
    return true;
  }), [rows, selected, checkboxStates, todayKey]);

  const cells = buildCells(cursor, visibleRows, notes);

  const removeNote = async (note) => {
    if (!window.confirm(`Delete note "${note.title}"?`)) return;
    try {
      await adminDeleteCalendarNote(note.id);
      showToast("success", "Note deleted");
      setOpenNotes(null);
      notesApi.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const selectedEmployee = employeeRows.find((e) => String(e.id) === String(employeeId));
  const selectedBusiness = businessRows.find((b) => String(b.id) === String(businessId));
  const subtitleParts = [];
  if (selectedBusiness) {
    const label = selectedBusiness.client_id
      ? `${selectedBusiness.client_id} · ${selectedBusiness.business_name}`
      : selectedBusiness.business_name;
    subtitleParts.push(`Client: ${label}`);
  }
  if (employeeId === "__unassigned__") subtitleParts.push("Unassigned");
  else if (selectedEmployee) subtitleParts.push(`Assigned to ${selectedEmployee.name}`);
  const subtitle = subtitleParts.length ? subtitleParts.join(" · ") : "All tasks across the firm";

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Calendar</h1>
          <div className="page-sub">{subtitle}</div>
        </div>
        <div className="page-actions">
          <SearchableSelect
            value={businessId}
            onChange={setBusinessId}
            options={businessOptions}
            placeholder="All clients"
            searchPlaceholder="Search clients…"
            width={220}
          />
          <SearchableSelect
            value={employeeId}
            onChange={setEmployeeId}
            options={employeeOptions}
            placeholder="All employees"
            searchPlaceholder="Search employees…"
            width={180}
          />
          <button className="btn btn-primary" onClick={() => setNoteEditor({})}>
            <Icon name="plus" size={14} /> Add note
          </button>
          <button className="btn btn-secondary" onClick={() => setCursor(nowIst().startOf("month"))}>
            Today
          </button>
          <div
            style={{
              display: "inline-flex",
              alignItems: "stretch",
              border: "1px solid var(--border-strong)",
              borderRadius: 7,
              overflow: "hidden",
              background: "var(--surface)",
            }}
          >
            <button
              type="button"
              onClick={() => setCursor(cursor.subtract(1, "month"))}
              style={{ padding: "0 10px", color: "var(--ink-700)", borderRight: "1px solid var(--border)" }}
              aria-label="Previous month"
            >
              <Icon name="chevron-left" size={14} />
            </button>
            <div
              style={{
                padding: "0 14px",
                display: "flex",
                alignItems: "center",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink-900)",
                minWidth: 130,
                justifyContent: "center",
              }}
            >
              {cursor.format("MMMM YYYY")}
            </div>
            <button
              type="button"
              onClick={() => setCursor(cursor.add(1, "month"))}
              style={{ padding: "0 10px", color: "var(--ink-700)", borderLeft: "1px solid var(--border)" }}
              aria-label="Next month"
            >
              <Icon name="chevron-right" size={14} />
            </button>
          </div>
        </div>
      </div>

      <CalendarLegend counts={countByState(rows, todayKeyIst())} selected={selected} onToggle={toggleState} />

      {tasks.loading ? (
        <LoadingBlock rows={1} height={400} />
      ) : (
        <div className="cal-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="cal-head">{d}</div>
          ))}
          {cells.map((c, i) => {
            const visibleTasks = c.tasks.slice(0, MAX_VISIBLE);
            const overflow = c.tasks.length - visibleTasks.length;
            return (
              <div key={i} className={`cal-cell ${c.muted ? "muted" : ""} ${c.today ? "today" : ""}`}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                  <span className="cal-date">
                    {c.date.date()}
                    {c.tasks.length > 0 && (
                      <span className="text-xs t-muted" style={{ marginLeft: 6, fontWeight: 500 }}>
                        {c.tasks.length}
                      </span>
                    )}
                  </span>
                  {c.notes.length > 0 && (
                    <button
                      type="button"
                      title={`${c.notes.length} note${c.notes.length === 1 ? "" : "s"}`}
                      onClick={() => setOpenNotes({ date: c.date, notes: c.notes })}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer",
                        background: "var(--warning-100)", color: "var(--warning)",
                        border: "1px solid #fde68a", borderRadius: 999,
                        padding: "0 6px", height: 18, fontSize: 11, fontWeight: 600,
                      }}
                    >
                      <Icon name="file-text" size={10} />
                      {c.notes.length}
                    </button>
                  )}
                </div>
                {visibleTasks.map((t) => {
                  const state = taskState(t);
                  const overdue = (t.due_date || "").slice(0, 10) < todayKeyIst()
                    && t.status !== "completed" && t.status !== "cancelled";
                  return (
                    <div
                      key={t.id}
                      className={`cal-task ${state} ${overdue ? "overdue" : ""}`}
                      title={`${t.task_master?.name || ""} · ${t.business?.business_name || ""}${t.employee ? " · " + t.employee.name : ""} · ${STATUS_LABELS[state]}${overdue ? " · overdue" : ""}`}
                      onClick={() => setOpenTask(t.id)}
                    >
                      {t.task_master?.name}
                      {t.business?.business_name && (
                        <span style={{ opacity: 0.7 }}> · {t.business.business_name}</span>
                      )}
                    </div>
                  );
                })}
                {overflow > 0 && (
                  <button
                    type="button"
                    className="cal-more"
                    onClick={() => setOpenDay({ date: c.date, tasks: c.tasks })}
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <DayTasksDrawer
        open={!!openDay}
        date={openDay?.date}
        tasks={openDay?.tasks || []}
        onClose={() => setOpenDay(null)}
        onTaskClick={(id) => { setOpenDay(null); setOpenTask(id); }}
      />

      <TaskDrawer
        taskId={openTask}
        role="admin"
        onClose={() => setOpenTask(null)}
        onChanged={() => tasks.reload()}
      />

      <DayNotesDrawer
        open={!!openNotes}
        date={openNotes?.date}
        notes={openNotes?.notes || []}
        canManage
        onClose={() => setOpenNotes(null)}
        onAdd={(date) => {
          const d = date?.format("YYYY-MM-DD");
          setOpenNotes(null);
          setNoteEditor({ start: d, end: d });
        }}
        onEdit={(note) => { setOpenNotes(null); setNoteEditor({ note }); }}
        onDelete={removeNote}
      />

      <CalendarNoteEditorDrawer
        open={!!noteEditor}
        admin
        employees={employeeRows}
        note={noteEditor?.note}
        defaultStart={noteEditor?.start}
        defaultEnd={noteEditor?.end}
        onClose={() => setNoteEditor(null)}
        onSaved={() => { setNoteEditor(null); notesApi.reload(); }}
      />
    </>
  );
}

function buildCells(cursor, tasks, notes = []) {
  const start = cursor.startOf("month");
  const startDow = start.day();
  const today = nowIst().startOf("day");
  const cells = [];
  const firstCellDate = start.subtract(startDow, "day");

  // Bucket tasks by due_date once. Backend may return either "YYYY-MM-DD" or
  // an ISO timestamp like "2026-05-17T00:00:00.000000Z"; slice the date portion.
  const byDate = new Map();
  for (const t of tasks) {
    const key = (t.due_date || "").slice(0, 10);
    if (!key) continue;
    if (!byDate.has(key)) byDate.set(key, []);
    byDate.get(key).push(t);
  }

  for (let i = 0; i < 42; i++) {
    const d = firstCellDate.add(i, "day");
    const muted = d.month() !== start.month();
    const key = d.format("YYYY-MM-DD");
    const dayTasks = byDate.get(key) || [];
    const dayNotes = notes.filter((n) => {
      const s = (n.start_date || "").slice(0, 10);
      const e = (n.end_date || "").slice(0, 10);
      return s && e && s <= key && key <= e;
    });
    cells.push({ date: d, muted, today: d.isSame(today, "day"), tasks: dayTasks, notes: dayNotes });
  }
  return cells;
}
